import type { ITheme } from '@vanialabs/ui-engine';

const CSS_VAR_PREFIX = '--vania';

type TokenLeaf = string | number;
type TokenNode = TokenLeaf | { [key: string]: TokenNode };

function toKebabCase(segment: string): string {
  return segment.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function flattenTokens(
  node: TokenNode,
  path: string[],
  out: Map<string, string>
): void {
  if (typeof node === 'string' || typeof node === 'number') {
    const varName = `${CSS_VAR_PREFIX}-${path.map(toKebabCase).join('-')}`;
    out.set(varName, String(node));
    return;
  }

  if (Array.isArray(node)) return;

  for (const [key, value] of Object.entries(node)) {
    flattenTokens(value, [...path, key], out);
  }
}

export function applyThemeCssVariables(theme: ITheme): void {
  console.log('[TRACE] applyThemeCssVariables: called with theme', theme);

  if (!theme || !theme.tokens) {
    console.error('[TRACE] applyThemeCssVariables: theme or theme.tokens is missing — aborting, no vars will be written', theme);
    return;
  }

  const root = document.documentElement;
  const variables = new Map<string, string>();

  flattenTokens(
    theme.tokens as unknown as TokenNode,
    [],
    variables
  );

  console.log(`[TRACE] applyThemeCssVariables: flattened ${variables.size} token variable(s)`, Array.from(variables.keys()));

  const colors = theme.tokens.colors;

  // New token contract
  variables.forEach((value, name) => {
    root.style.setProperty(name, value);
  });

  if (!colors) {
    console.error(
      '[TRACE] applyThemeCssVariables: theme.tokens.colors is missing',
      theme.tokens
    );
    return;
  }

  /**
   * Representative Admin color aliases
   * Required by Tailwind classes.
   */
  root.style.setProperty(
    '--vania-colors-background-primary',
    colors.background.primary
  );

  root.style.setProperty(
    '--vania-colors-background-secondary',
    colors.background.secondary
  );

  root.style.setProperty(
    '--vania-colors-surface-primary',
    colors.surface.primary
  );

  root.style.setProperty(
    '--vania-colors-surface-secondary',
    colors.surface.secondary
  );

  root.style.setProperty(
    '--vania-colors-text-primary',
    colors.text.primary
  );

  root.style.setProperty(
    '--vania-colors-text-secondary',
    colors.text.secondary
  );

  root.style.setProperty(
    '--vania-colors-text-disabled',
    colors.text.disabled
  );

  root.style.setProperty(
    '--vania-colors-border-default',
    colors.border.default
  );

  root.style.setProperty(
    '--vania-colors-border-subtle',
    colors.border.subtle
  );

  root.style.setProperty(
    '--vania-colors-action-primary',
    colors.action.primary
  );

  /**
   * Legacy Vania Admin aliases
   * Keeps existing dashboard/components compatible.
   */
  root.style.setProperty(
    '--vania-bg-deep',
    colors.background.primary
  );

  root.style.setProperty(
    '--vania-bg-input',
    colors.background.secondary
  );

  root.style.setProperty(
    '--vania-surface',
    colors.surface.primary
  );

  root.style.setProperty(
    '--vania-surface-1',
    colors.surface.primary
  );

  root.style.setProperty(
    '--vania-surface-2',
    colors.surface.secondary
  );

  root.style.setProperty(
    '--vania-surface-elevated',
    colors.surface.elevated
  );

  root.style.setProperty(
    '--vania-text-primary',
    colors.text.primary
  );

  root.style.setProperty(
    '--vania-text-secondary',
    colors.text.secondary
  );

  root.style.setProperty(
    '--vania-text-tertiary',
    colors.text.tertiary
  );

  root.style.setProperty(
    '--vania-text-muted',
    colors.text.disabled
  );

  root.style.setProperty(
    '--vania-border',
    colors.border.default
  );

  root.style.setProperty(
    '--vania-border-glass-strong',
    colors.border.default
  );

  root.style.setProperty(
    '--vania-border-glass',
    colors.border.subtle
  );

  root.style.setProperty(
    '--vania-accent',
    colors.action.primary
  );

  root.style.setProperty(
    '--vania-accent-violet',
    colors.action.primary
  );

  root.style.setProperty(
    '--vania-accent-teal',
    colors.feedback.success
  );

  root.style.setProperty(
    '--vania-accent-rose',
    colors.feedback.error
  );

  root.style.setProperty(
    '--vania-accent-amber',
    colors.feedback.warning
  );

  /**
   * Dashboard legacy aliases
   * Keeps dashboard components compatible with ThemeEngine tokens.
   */
  root.style.setProperty(
    '--vania-surface',
    colors.surface.primary
  );

  root.style.setProperty(
    '--vania-surface-2',
    colors.surface.secondary
  );

  root.style.setProperty(
    '--vania-surface-3',
    colors.surface.elevated
  );

  root.style.setProperty(
    '--vania-surface-solid',
    colors.surface.primary
  );

  root.style.setProperty(
    '--vania-border-glass',
    colors.border.subtle
  );

  root.style.setProperty(
    '--vania-border-glass-strong',
    colors.border.default
  );

  root.style.setProperty(
    '--vania-accent-violet',
    colors.action.primary
  );

  root.style.setProperty(
    '--vania-accent-teal',
    colors.feedback.success
  );

  root.style.setProperty(
    '--vania-accent-amber',
    colors.feedback.warning
  );

  root.style.setProperty(
    '--vania-accent-rose',
    colors.feedback.error
  );

  root.style.setProperty(
    '--vania-text-tertiary',
    colors.text.tertiary
  );
}
